import React, { PropsWithChildren, ReactNode, useState } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { Box, Chip, Collapse, IconButton, Typography } from '@mui/material';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';
import { grey, lightGreen, red } from '@mui/material/colors';

interface Props {
  title: ReactNode;
  isExpanded: boolean;
  isExpandable: boolean;
  totalStatus?: Record<'current' | 'total', number>;
  requiredStatus?: Record<'current' | 'total', number>;
}

export const FormSection = ({
  title,
  children,
  totalStatus,
  requiredStatus,
  isExpanded = true,
  isExpandable = false,
}: PropsWithChildren<Partial<Props>>) => {
  const classes = useStyles();

  const [isSectionExpanded, setIsSectionExpanded] = useState<boolean>(isExpanded);

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Box>
          {isExpandable && (
            <IconButton
              size="small"
              onClick={event => {
                event.stopPropagation();
                isExpandable && setIsSectionExpanded(!isSectionExpanded);
              }}
            >
              {isSectionExpanded ? (
                <RemoveIcon className={classes.expandIcon} />
              ) : (
                <AddIcon className={classes.expandIcon} />
              )}
            </IconButton>
          )}

          {title && (
            <Typography variant="subtitle2" component="span">
              {title}
            </Typography>
          )}
        </Box>

        <Box display="flex">
          {requiredStatus && (
            <Chip
              size="small"
              label={
                <Typography variant="body2" fontWeight={500}>
                  {`${requiredStatus.current}/${requiredStatus.total} Required`}
                </Typography>
              }
              sx={
                requiredStatus.total === requiredStatus.current
                  ? { backgroundColor: lightGreen[100], color: lightGreen[800] }
                  : { backgroundColor: '#FEE1E4', color: red[600] }
              }
            />
          )}

          {totalStatus && (
            <Typography variant="subtitle2" component="span" sx={{ marginLeft: 1, color: 'rgba(0, 0, 0, 0.6)' }}>
              {`${totalStatus.current}/${totalStatus.total} Total`}
            </Typography>
          )}
        </Box>
      </Box>

      <Collapse in={isSectionExpanded}>
        <Box padding={2}>{children}</Box>
      </Collapse>
    </Box>
  );
};

const useStyles = makeStyles(theme => ({
  root: {
    border: `1px solid ${grey[300]}`,
    borderRadius: 1,
  },
  header: {
    gap: 1,
    width: '100%',
    display: 'flex',
    padding: '8px 12px',
    alignItems: 'center',
    backgroundColor: '#F5F5F5',
    justifyContent: 'space-between',
  },
  expandIcon: {
    fontSize: 22,
    transition: '0.5s',
  },
}));
